/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badges, H6, LI, UL } from '../../AbstractElements'
import { SidebarItemTypes } from '../../Types/Layout.type'
import { RootState } from '../../ReduxToolkit/Store';
import { handlePined } from '../../ReduxToolkit/Reducers/Layout/LayoutReducer';
import { Href } from '../../Utils/Constants';
import SvgIcon from '../../Utils/CommonComponents/CommonSvgIcons';
import { useAuth } from '../../Context/AuthContext';
type SidebarItem = {
    title?: string;
    path?: string;
    icon?: string;
    children?: SidebarItem[];
    badge?: boolean;
    badgeName?: string;
    type?: string;
};

export type MenuListType = {
    menu: SidebarItem[] | undefined;
    setActiveMenu: React.Dispatch<React.SetStateAction<string[]>>;
    activeMenu: string[];
    level: number;
    className?: string;
    onItemClick?: (item: SidebarItem) => void;
};
export default function SidebarSubMenu({
    menu,
    setActiveMenu,
    activeMenu,
    level,
    className,
    onItemClick
}: MenuListType) {
    const dispatch = useDispatch();
    const { hasPermission } = useAuth();

    // Helper to check recursive visibility
    const checkVisibility = (item: any): boolean => {
        // 1. Check direct permission with implied alternatives
        if (item.permission) {
            if (!hasPermission(item.permission)) {
                if (item.permission.endsWith('.view')) {
                    const moduleBase = item.permission.replace('.view', '');
                    const alternatives = ['.add', '.edit', '.delete', '.approve', '.transfer', '.download', '.import'];
                    const hasAlternative = alternatives.some(action => hasPermission(`${moduleBase}${action}`));
                    if (!hasAlternative) return false;
                } else {
                    return false;
                }
            }
        }

        // 2. Check children visibility
        if (item.children && item.children.length > 0) {
            const hasVisibleChild = item.children.some((child: any) => checkVisibility(child));

            // If parent is a container (type 'sub') and has no visible children, hide it
            if (item.type === 'sub' && !hasVisibleChild) {
                return false;
            }
        }

        return true;
    };

    // Filter menu based on permissions
    const filteredMenu = menu?.filter(item => checkVisibility(item));

    const { t } = useTranslation();
    const { pinedMenu } = useSelector((state: RootState) => state.layout);
    const location = useLocation();
    const ActiveNavLinkUrl = (path?: string, active?: boolean) => {
        if (!path) return "";
        if (location.pathname === path) return active ? active : true;
        if (location.pathname.startsWith(path + "/")) return active ? active : true;

        // Check for add_ and edit_ variations to highlight parent menu
        // Example: path="/driver" -> matches "/add_driver", "/edit_driver", "/view_driver"
        const resourceName = path.split('/').pop();
        if (resourceName) {
            if (location.pathname.includes(`/add_${resourceName}`) ||
                location.pathname.includes(`/edit_${resourceName}`) ||
                location.pathname.includes(`/view_${resourceName}`)) {
                return active ? active : true;
            }
        }
        return "";
    };
    const shouldSetActive = ({ item }: SidebarItemTypes) => {
        var returnValue = false;
        if (item?.path && (item.path === location.pathname || location.pathname.startsWith(item.path + "/"))) returnValue = true;

        if (!returnValue && item?.path) {
            const resourceName = item.path.split('/').pop();
            if (resourceName) {
                if (location.pathname.includes(`/add_${resourceName}`) ||
                    location.pathname.includes(`/edit_${resourceName}`) ||
                    location.pathname.includes(`/view_${resourceName}`)) {
                    returnValue = true;
                }
            }
        }

        if (!returnValue && item?.children) {
            item?.children.every((subItem) => {
                returnValue = shouldSetActive({ item: subItem });
                return !returnValue;
            });
        }
        return returnValue;
    };
    const handleClick = ((item: string) => {
        const temp = activeMenu;
        temp[level] = item !== temp[level] ? item : "";
        setActiveMenu([...temp]);
    })
    useEffect(() => {
        menu?.forEach((item: any) => {
            let gotValue = shouldSetActive({ item });
            if (gotValue) {
                let temp = activeMenu;
                temp[level] = item.title;
                setActiveMenu(temp);
            }
        });
    }, []);

    return (
        <>
            {filteredMenu?.map((item, index) => (
                <LI key={index} className={`${level === 0 ? "sidebar-list" : ""} ${pinedMenu.includes(item.title || "") ? "pined" : ""}  
                ${(item.children
                        ? item.children.map((innerItem) => ActiveNavLinkUrl(innerItem.path)).includes(true)
                        : ActiveNavLinkUrl(item.path))
                        ? "active"
                        : ""
                    } `}
                >
                    {level === 0 && (<i className="fa-solid fa-thumbtack" onClick={() => dispatch(handlePined(item.title))} />)}
                    <Link
                        className={`${!className && level !== 2 ? "sidebar-link " : item.type === "sub" ? "submenu-title" : ""} 
                        ${(item.children
                                ? item.children.map((innerItem) => ActiveNavLinkUrl(innerItem.path)).includes(true)
                                : ActiveNavLinkUrl(item.path)) ? "active" : ""
                            }`}
                        to={item.path ? item.path : Href}
                        onClick={() => {
                            handleClick(item.title || "");
                            if (!item.children || item.children.length === 0) {
                                onItemClick?.(item);
                            }
                        }}
                    >
                        {item.icon && (
                            <SvgIcon className='stroke-icon' iconId={item.icon} />
                        )}
                        <H6>{(`${item.title}`)}</H6>
                        {item.badge ? <Badges color='primary'>{item.badgeName}</Badges> : ''}
                        {item.children && (activeMenu[level] === item.title ? (
                            <i className="iconly-Arrow-Down-2 icli"></i>
                        ) : (
                            <i className="iconly-Arrow-Right-2 icli"></i>
                        ))}
                    </Link>
                    {item.children && (
                        <UL className={`simple-list ${level !== 0 ? "nav-sub-childmenu submenu-content" : "sidebar-submenu "}`} style={{
                            display: `${(item.children
                                ? item.children.map((innerItem) => ActiveNavLinkUrl(innerItem.path)).includes(true) : ActiveNavLinkUrl(item.path)) || activeMenu[level] === item.title ? "block" : "none"
                                }`
                        }}
                        >
                            <SidebarSubMenu menu={item.children} activeMenu={activeMenu} setActiveMenu={setActiveMenu} level={level + 1} className="sidebar-submenu" onItemClick={onItemClick} />
                        </UL>
                    )}
                </LI >
            ))
            }
        </>
    )
}
